import { useEffect } from 'react';

import useFeatureFlag from '@/utils/useFeatureFlag';
import palette from '@/themeV2/palette';
import typography from '@/themeV2/typography';
import { inputStyles } from '@/themeV2/components';

const v3UIStyles = {
  colors: {
    background: palette.background.base,
    primary: palette.background.primary,
    secondary: palette.secondary.main,
    success: palette.status.successForeground,
    warning: palette.status.warningForeground,
    error: palette.status.errorForeground,
  },
  typography: {
    common: {
      family: 'Inter, sans-serif',
      color: palette.text.primary,
      size: '14px',
    },
    headings: {
      pageHeading: {
        color: palette.text.primary,
        family: 'Inter, sans-serif',
        size: '32px',
        weight: '800',
      },
      pageSubheading: {
        color: '#6c6c6c',
        family: 'Inter, sans-serif',
        size: '18px',
        weight: '400',
      },
      sectionHeading: {
        color: palette.background.primary,
        family: 'Inter, sans-serif',
        size: '24px',
        weight: '800',
      },
      sectionSubheading: {
        color: '#555',
        family: 'Inter, sans-serif',
        size: '14px',
        weight: '400',
      },
    },
    formInputLabels: {
      color: '#222',
      family: 'Inter, sans-serif',
      size: '14px',
      weight: '400',
    },
  },
  buttons: {
    primary: {
      default: {
        backgroundColor: palette.background.primary,
        border: {
          color: palette.background.base,
          radius: '8px',
          width: '3px',
        },
        font: {
          color: palette.text.primaryInverse,
          family: 'Inter, sans-serif',
          size: '16px',
          weight: '600',
        },
      },
      hover: {
        backgroundColor: '#4d45ba',
        border: {
          color: '#fff',
          radius: '8px',
          width: '3px',
        },
        font: {
          color: '#fff',
        },
      },
      active: {
        backgroundColor: palette.background.primary,
        border: {
          color: '#beb9f8',
          radius: '8px',
          width: '3px',
        },
        font: {
          color: '#fff',
        },
      },
      disabled: {
        backgroundColor: '#d4d6d6',
        border: {
          color: '#fff',
          radius: '8px',
          width: '3px',
        },
        font: {
          color: '#fff',
        },
      },
    },
  },
  formInputs: {
    default: {
      backgroundColor: palette.background.base,
      border: {
        color: '#d4d6d6',
        radius: '4px',
        width: '2px',
      },
      font: {
        color: '#000',
        placeholderColor: '#8e8e8e',
        size: '14px',
        weight: '400',
      },
    },
    hover: {
      backgroundColor: palette.background.base,
      border: {
        color: '#d4d6d6',
        radius: '4px',
        width: '2px',
      },
      font: {
        color: '#000',
      },
    },
    active: {
      backgroundColor: palette.background.base,
      border: {
        color: palette.background.primary,
        radius: '4px',
        width: '2px',
      },
      font: {
        color: '#000',
      },
    },
    disabled: {
      backgroundColor: '#e3e6e6',
      border: {
        color: '#d4d6d6',
        radius: '4px',
        width: '2px',
      },
      font: {
        color: '#8e8e8e',
      },
    },
  },
};

const customTheme = {
  text: {
    componentLabelSize: '14px',
    componentLabelWeight: 'bold',
    componentSize: '12px',
    fontFamily: typography.fontFamily,
    size: typography.bodymedium.fontSize,
    weight: typography.bodymedium.fontWeight,
  },
  shape: {
    componentBorderWidth: '1px',
    componentCornerRadius: inputStyles.borderRadius,
  },
  color: {
    background: palette.background.base,
    componentBackground: palette.background.base,
    componentBorder: palette.border.input,
    componentPlaceholderText: palette.text.placeholder,
    componentText: palette.text.primary,
    danger: palette.status.errorForeground,
    primary: palette.border.input,
    text: palette.text.primary,
  },
  button: {
    color: {
      activeBackground: palette.background.primary,
      disabledBackground: '#BFBFCA',
      disabledText: palette.text.primaryInverse,
      loadingBackground: '#e0e0e0',
      loadingBorder: '#e0e0e0',
      loadingText: palette.text.placeholder,
    },
    shape: {
      borderWidth: '1px',
      cornerRadius: inputStyles.borderRadius,
    },
    text: {
      fontFamily: typography.fontFamily,
      size: typography.buttonextralarge.fontSize,
      weight: typography.buttonextralarge.fontWeight,
    },
  },
};

const customContent = {
  submitButton: 'Continue',
  loadingButton: 'Verifying',
  loadingPrompt: 'Please wait',
};

const useAbound = ({
  targetId,
  accessToken,
  onSubmitSuccess,
  onSubmit,
  onLoadError,
  onSubmitError,
}) => {
  const isV3Enabled = useFeatureFlag('use_abound_ui_v3');
  useEffect(() => {
    const fetchData = async () => {
      if (isV3Enabled) {
        const AboundClient = await import(
          // eslint-disable-next-line import/extensions,import/no-unresolved
          /* webpackIgnore: true */ 'https://js.withabound.com/latest-v3-minor/abound-client-sdk.js'
        );
        AboundClient.renderPayeeTaxProfile({
          accessToken,
          targetId,
          onSubmitSuccess,
          onSubmitError,
          onSubmit,
          styles: v3UIStyles,
        });
        return;
      }
      // temporary fallback if the v3 ui is not enabled

      const AboundClient = await import(
        // eslint-disable-next-line import/extensions,import/no-unresolved
        /* webpackIgnore: true */ 'https://js.withabound.com/latest-major/abound-client-sdk.js'
      );

      AboundClient.renderW9Collection({
        accessToken,
        targetId,
        onSubmitSuccess,
        onSubmitError,
        onSubmit,
        theme: customTheme,
        content: customContent,
      });
    };

    fetchData().catch(() => {
      if (onLoadError) {
        onLoadError();
      }
    });
  }, [
    accessToken,
    targetId,
    onSubmitSuccess,
    onSubmit,
    onLoadError,
    onSubmitError,
    isV3Enabled,
  ]);
};

export default useAbound;
